import { useState, useRef, useEffect, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, Save, Eraser, Download, FileText, Clock, User } from "lucide-react";
import { toast } from "sonner";
import {
  getDerogationTemplate, saveFlightDerogation, listFlightDerogations, getFlightDerogation, getAircraftRotation,
} from "@/lib/api";
import {
  PAGE_W, PAGE_H, FONT_SIZE, ALL_TEXT_FIELDS, SIGNATURE_FIELD,
} from "@/components/derogationFields";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const DISPLAY_W = 780;
const SCALE = DISPLAY_W / PAGE_W;

const b64ToBytes = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};
const bytesToB64 = (bytes) => {
  let bin = "";
  const arr = new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
  return btoa(bin);
};
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export const DerogationEditor = ({ flight, open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateBytes, setTemplateBytes] = useState(null);
  const [values, setValues] = useState({});
  const [versions, setVersions] = useState([]);
  const canvasRef = useRef(null);
  const sigCanvasRef = useRef(null);
  const printFrameRef = useRef(null);
  const drawing = useRef(false);
  const hasSignature = useRef(false);

  const loadVersions = useCallback(async () => {
    try { const r = await listFlightDerogations(flight.id); setVersions(r.data || []); } catch { /* ignore */ }
  }, [flight.id]);

  // Load template + prefill on open
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    hasSignature.current = false;
    (async () => {
      try {
        const res = await getDerogationTemplate();
        setTemplateBytes(b64ToBytes(res.data.data));
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const base = {
          date_demande: `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`,
          mv_datetime: `${flight.flight_date || ""} ${flight.time || ""}`.trim(),
          mv_compagnie: flight.operator || flight.airline || "",
          mv_type: flight.aircraft_type || "",
          mv_immat: flight.registration || "",
        };
        // Pre-fill Section 2 (rotation history) from the aircraft's rotation that day
        try {
          if (flight.registration) {
            const rot = await getAircraftRotation(flight.registration, flight.flight_date);
            const arr = rot.data?.last_arrival;
            const dep = rot.data?.last_departure;
            if (arr) {
              base.rot_0_0 = arr.flight_number || "";
              base.rot_0_4 = arr.origin_name || arr.origin || "";    // En provenance de (full airport name)
              base.rot_0_5 = arr.time || "";                         // Heure planifiée (arr)
              base.rot_0_6 = arr.ata || arr.eta || "";               // Heure estimée (arr)
              base.rot_0_7 = arr.origin || "";                       // Code IATA (4-letter code)
            }
            if (dep) {
              base.rot_1_0 = dep.flight_number || "";
              base.rot_1_1 = dep.destination_name || dep.destination || ""; // A destination de (full airport name)
              base.rot_1_2 = dep.time || "";                         // Heure planifiée (dep)
              base.rot_1_3 = dep.atd || dep.etd || dep.eta || "";    // Heure estimée (dep)
              base.rot_1_7 = dep.destination || "";                  // Code IATA (4-letter code)
            }
          }
        } catch (rotErr) { /* rotation prefill is best-effort */ }
        setValues(base);
        await loadVersions();
      } catch (e) {
        console.error(e);
        toast.error("Could not load the derogation template");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, flight, loadVersions]);

  // Render page 1 into the background canvas
  useEffect(() => {
    if (!templateBytes || loading || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ data: templateBytes.slice(0) }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: SCALE });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      } catch (e) { console.error("render error", e); }
    })();
    return () => { cancelled = true; };
  }, [templateBytes, loading]);

  // ---- Signature drawing ----
  const sigPos = (e) => {
    const c = sigCanvasRef.current;
    const rect = c.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * (c.width / rect.width), y: (clientY - rect.top) * (c.height / rect.height) };
  };
  const startDraw = (e) => { e.preventDefault(); drawing.current = true; const ctx = sigCanvasRef.current.getContext("2d"); const p = sigPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const moveDraw = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = sigCanvasRef.current.getContext("2d"); const p = sigPos(e); ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#0a1f6b"; ctx.lineTo(p.x, p.y); ctx.stroke(); hasSignature.current = true; };
  const endDraw = () => { drawing.current = false; };
  const clearSig = () => { const c = sigCanvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasSignature.current = false; };

  // Generate the filled PDF (Uint8Array)
  const buildPdf = async () => {
    const doc = await PDFDocument.load(templateBytes.slice(0));
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const page = doc.getPages()[0];
    const blue = rgb(0.04, 0.12, 0.42);
    ALL_TEXT_FIELDS.forEach((f) => {
      const v = values[f.id];
      if (v && String(v).trim()) {
        page.drawText(String(v), { x: f.x + 1, y: PAGE_H - f.top - FONT_SIZE, size: FONT_SIZE, font, color: blue });
      }
    });
    if (hasSignature.current && sigCanvasRef.current) {
      const png = await doc.embedPng(sigCanvasRef.current.toDataURL("image/png"));
      const s = SIGNATURE_FIELD;
      page.drawImage(png, { x: s.x, y: PAGE_H - s.top - s.h, width: s.w, height: s.h });
    }
    return doc.save();
  };

  const printBytes = (bytes) => {
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    const frame = printFrameRef.current;
    frame.src = url;
    frame.onload = () => { try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { window.open(url, "_blank"); } };
  };

  const handlePrint = async () => {
    try { printBytes(await buildPdf()); } catch (e) { console.error(e); toast.error("Could not prepare the document for printing"); }
  };

  const handleSave = async (alsoPrint) => {
    setSaving(true);
    try {
      const bytes = await buildPdf();
      const filename = `Derogation_${flight.flight_number || flight.registration || flight.id}.pdf`;
      await saveFlightDerogation(flight.id, { filename, data: bytesToB64(bytes) });
      toast.success("Derogation form saved to flight history");
      await loadVersions();
      if (alsoPrint) printBytes(bytes);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save the derogation form");
    } finally {
      setSaving(false);
    }
  };

  const openVersion = async (v, action) => {
    try {
      const r = await getFlightDerogation(flight.id, v.id);
      const bytes = b64ToBytes(r.data.data);
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      if (action === "print") { printBytes(bytes); }
      else if (action === "download") { const a = document.createElement("a"); a.href = url; a.download = v.filename || "derogation.pdf"; a.click(); }
      else { window.open(url, "_blank"); }
    } catch { toast.error("Could not open that version"); }
  };

  const fieldStyle = (f) => ({
    position: "absolute", left: f.x * SCALE, top: f.top * SCALE, width: f.w * SCALE, height: (f.h || 14) * SCALE,
    fontSize: Math.max(9, FONT_SIZE * SCALE - 1), lineHeight: 1, padding: "0 2px",
    border: "1px solid rgba(37,99,235,0.45)", background: "rgba(219,234,254,0.45)", color: "#0a1f6b",
    borderRadius: 2, outline: "none",
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white dark:bg-[#0f172a] max-w-[900px] max-h-[94vh] overflow-y-auto" data-testid="derogation-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0f172a] dark:text-white flex items-center gap-2">
            <FileText className="text-red-600" size={20} />
            Demande SPW Derogation — {flight.flight_number || flight.registration}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-10 w-10 animate-spin text-[#d4af37]" /></div>
        ) : (
          <div className="space-y-4">
            {versions.length > 0 && (
              <div className="rounded-lg border border-[#e2e8f0] dark:border-[#334155] p-3 bg-[#f8fafc] dark:bg-[#1e293b]" data-testid="derogation-versions">
                <p className="text-xs font-semibold text-[#475569] dark:text-slate-300 mb-2">Saved versions (in flight history)</p>
                <div className="space-y-1.5">
                  {versions.map((v) => (
                    <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#334155] dark:text-slate-300">
                      <span className="flex items-center gap-2">
                        <FileText size={13} className="text-red-500" /> v{v.version}
                        <span className="flex items-center gap-1"><Clock size={11} /> {fmtDate(v.created_at)}</span>
                        <span className="flex items-center gap-1"><User size={11} /> {v.created_by}</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openVersion(v, "open")} data-testid={`derog-version-open-${v.id}`}>Open</Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openVersion(v, "print")}><Printer size={12} /></Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openVersion(v, "download")}><Download size={12} /></Button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-[#64748b] dark:text-slate-400">
              Fill the highlighted fields on page 1, then Save (stores a new version in this flight's history) or Print.
            </p>

            {/* Page 1 editable overlay */}
            <div className="relative mx-auto shadow-lg" style={{ width: DISPLAY_W, height: PAGE_H * SCALE }} data-testid="derogation-page1">
              <canvas ref={canvasRef} className="absolute top-0 left-0" />
              {ALL_TEXT_FIELDS.map((f) => (
                <input
                  key={f.id}
                  type="text"
                  value={values[f.id] || ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [f.id]: e.target.value }))}
                  style={fieldStyle(f)}
                  data-testid={`derog-field-${f.id}`}
                />
              ))}
              {/* Signature pad */}
              <div style={{ position: "absolute", left: SIGNATURE_FIELD.x * SCALE, top: SIGNATURE_FIELD.top * SCALE, width: SIGNATURE_FIELD.w * SCALE, height: SIGNATURE_FIELD.h * SCALE }}>
                <canvas
                  ref={sigCanvasRef}
                  width={SIGNATURE_FIELD.w * 2}
                  height={SIGNATURE_FIELD.h * 2}
                  className="w-full h-full rounded-sm cursor-crosshair"
                  style={{ border: "1px dashed rgba(37,99,235,0.6)", background: "rgba(219,234,254,0.35)", touchAction: "none" }}
                  onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
                  data-testid="derog-signature-pad"
                />
                <button type="button" onClick={clearSig} className="absolute -top-5 right-0 text-[10px] text-[#64748b] flex items-center gap-0.5 hover:text-red-600" data-testid="derog-signature-clear">
                  <Eraser size={10} /> clear
                </button>
              </div>
            </div>
          </div>
        )}

        <iframe ref={printFrameRef} title="print-frame" style={{ display: "none" }} />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="derogation-editor-close-btn">Close</Button>
          <Button variant="outline" onClick={handlePrint} disabled={loading || saving} className="gap-2" data-testid="derogation-editor-print-btn">
            <Printer size={16} /> Print
          </Button>
          <Button onClick={() => handleSave(false)} disabled={loading || saving} className="bg-[#0f172a] hover:bg-[#1e293b] text-white gap-2" data-testid="derogation-editor-save-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
          </Button>
          <Button onClick={() => handleSave(true)} disabled={loading || saving} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="derogation-editor-save-print-btn">
            <Printer size={16} /> Save &amp; Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
